import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Alert } from '../ui/Alert';
import { Sheet, SheetBody, SheetContent, SheetTrigger } from '../ui/Sheet';
import { apiFetch } from '../../lib/api';
import { htmlPreviewDocument } from '../html-template/previewDocument';
import { ApiError, type ContactPreview } from '../../types/api';

/**
 * "See as a contact": shows the mail the way one existing contact would
 * get it (merge tags, language, show/hide rules). Nothing is sent. The e-mail
 * address stays in this form only; it is not stored in the page, the URL or
 * the query cache.
 */
export function PreviewAsContact({ html, subject }: { html: string; subject: string }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      apiFetch<ContactPreview>('/contact-preview', {
        method: 'POST',
        body: { email: email.trim(), subject, html },
      }),
  });

  const error =
    mutation.error instanceof ApiError
      ? mutation.error.status === 404
        ? 'No contact with that e-mail address.'
        : (mutation.error.body.message ?? 'The preview could not be made.')
      : mutation.isError
        ? 'The preview could not be made.'
        : null;
  const result = mutation.data;

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          mutation.reset();
          setEmail('');
        }
      }}
    >
      <SheetTrigger asChild>
        <button type="button" className="pf-btn pf-btn-sm">
          See as a contact
        </button>
      </SheetTrigger>
      <SheetContent
        title="See as a contact"
        description="See this email the way one contact would get it. Nothing is sent."
      >
        <form
          className="flex min-h-0 flex-grow flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
        >
          <SheetBody>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="preview-contact-email">Contact e-mail address</Label>
              <Input
                id="preview-contact-email"
                type="email"
                required
                autoComplete="off"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={mutation.isPending || email.trim() === ''}>
              {mutation.isPending ? 'Rendering…' : 'Show preview'}
            </Button>
            {error ? <Alert>{error}</Alert> : null}
            {result ? (
              <div className="flex flex-col gap-3" role="status">
                <p className="text-sm">
                  {[
                    [result.contact.firstName, result.contact.lastName].filter(Boolean).join(' ') ||
                      'No name',
                    result.contact.language ? `language ${result.contact.language}` : 'no language',
                    result.contact.country ?? 'no country',
                  ].join(' · ')}
                  {result.contact.suppressed ? ' · suppressed (would not receive this email)' : ''}
                </p>
                <p className="text-sm">
                  <strong>Subject:</strong> {result.subject}
                </p>
                {result.rules.length > 0 ? (
                  <ul className="text-sm">
                    {result.rules.map((r) => (
                      <li key={r.rule}>
                        <code>{r.rule}</code>: {r.shown ? 'shown' : 'hidden'}
                      </li>
                    ))}
                  </ul>
                ) : null}
                <iframe
                  title="Email as this contact"
                  sandbox=""
                  srcDoc={htmlPreviewDocument(result.html)}
                  style={{ width: '100%', height: 480, border: '1px solid var(--pf-border, #ddd)' }}
                />
              </div>
            ) : null}
          </SheetBody>
        </form>
      </SheetContent>
    </Sheet>
  );
}
